import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useT } from 'talkr';
import aes from 'crypto-js/aes';
import questions from '../../../survey/questions';
import steps from '../../../survey/steps';
import s from './index.module.scss';
import common from '../../index.module.scss';
import validateEmail from '../../../utils/scripts/validateEmail';
import getLinkToPaymentPage from '../../../utils/scripts/getLinkToPaymentPage';
import store from '../../../store';

const domains = [
  '@gmail.com',
  '@yahoo.com',
  '@hotmail.com',
  '@icloud.com',
  '@aol.com',
  '@outlook.com',
  '@hotmail.co.uk',
  '@live.com',
  '@yahoo.co.uk',
  '@msn.com',
];

function Mail(props) {
  const { T } = useT();
  const { isThisCurrentStep } = props;
  const { isCurrentStepCompleted } = props;
  const isThisCurrentStepClass = isThisCurrentStep ? s.stepSelected : '';
  const isThisStepCompletedClass = isCurrentStepCompleted ? s.stepCompleted : '';
  const currentStep = useSelector((state) => state.steps.currentStep);
  const placeholder = T('email_placeholder');
  const emailWarningShort = T('customV1_email_warning_short');
  const emailWarningLong = T('customV1_email_warning_long');
  const emailWarning = T('customV1_email_warning');
  const savedQuestionValues = useSelector((state) => state.questions.savedAnswers.mail);
  const list = useSelector((state) => state.steps.list);
  const savedValue = savedQuestionValues && savedQuestionValues.value;
  const [value, setValue] = useState(savedValue || '');
  const [validationWarning, setValidation] = useState(false);
  const mail = questions.getAnswers({ questionName: 'mail' })?.value;
  const paymentPageVersion = '&everydoggyV1=true';
  const link = getLinkToPaymentPage();
  const currentStepCompleted = useSelector((state) => state.steps.currentStepCompleted);
  const [email, setEmail] = useState('');
  const [suggestions, setSuggestions] = useState(domains);
  const [domainSelected, setDomainSelected] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputFocusRef = useRef(null);
  const buttonRef = useRef(null);

  function createUrlDoggyData(arrAnswers) {
    let urlDoggyData = '';
    arrAnswers.forEach((key) => {
      const objAnswer = store.getState().questions.savedAnswers[key];
      const strAnswer = Object.keys(objAnswer).join('_');
      urlDoggyData += `&${key}=${strAnswer}`;
    });

    return urlDoggyData;
  }

  function createUrlABTestData() {
    const urlABTestData = '';
    return urlABTestData;
  }

  const arrAnswers = [
    'customV1_breed',
    'customV1_commands',
    'customV1_concern',
    'customV1_main_goal',
    'customV1_training_time',
  ];

  const urlDoggyData = createUrlDoggyData(arrAnswers);
  const urlABTestData = createUrlABTestData();

  let finalEmail;
  if (mail) {
    const emailValue = aes.encrypt(mail, '9213812384').toString();
    const emailValueUrl = encodeURIComponent(emailValue);
    finalEmail = `&ev=${emailValueUrl}`;
  }
  const resultLink = link + finalEmail + urlDoggyData + paymentPageVersion + urlABTestData;

  function validate() {
    if (value && (value.length >= 63)) {
      setValidation(emailWarningLong);
      return;
    }

    if (value && (value.length <= 5)) {
      setValidation(emailWarningShort);
      return;
    }

    if (!validateEmail(value) && value !== '') {
      setValidation(emailWarning);
      return;
    }

    setValidation(validateEmail(value));
  }

  useEffect(() => {
    validate();
  }, [value, currentStep]);

  function onChangeAction(e) {
    setValue(e.target.value);

    questions.setAnswer({
      questionName: 'mail',
      answerName: 'value',
      answerValue: e.target.value,
    });
  }

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setEmail(inputValue);
    if (inputValue.length >= 3 && !domainSelected) {
      setShowSuggestions(true);
      if (inputValue.indexOf('@') !== -1) {
        const domainInput = inputValue.substring(inputValue.indexOf('@') + 1);
        const filteredDomains = domains.filter((domain) => domain.includes(domainInput));
        setSuggestions(filteredDomains);
      }
    } else if (inputValue.indexOf('@') === -1 && domainSelected) {
      setShowSuggestions(true);
      setDomainSelected(false);
    } else {
      setShowSuggestions(false);
    }

    onChangeAction(e);
  };

  const handleSuggestionClick = (suggestion) => {
    setEmail(`${email.split('@')[0]}${suggestion}`);
    setSuggestions([]);
    setShowSuggestions(false);

    onChangeAction({ target: { value: `${email.split('@')[0]}${suggestion}` } });
    setTimeout(() => {
      inputFocusRef.current.blur();
      buttonRef.current
        .removeAttribute('disabled');
      buttonRef.current.focus();
    }, 100);

    setDomainSelected(true);
  };

  useEffect(() => {
    const completed = (value !== '' && validationWarning === true);

    steps.setStepStatus({
      stepName: 'customV1_email',
      stepStatus: completed,
    });
  }, [value, list, currentStep, validationWarning]);

  function actionOnFocus() {
    setTimeout(() => {
      document.documentElement.scrollTop = -300;
    }, 50);
  }

  function goToPaymentPage() {
    steps.analytics.send({
      event: {
        event: 'onboarding_question',
        quiz_question: 'send_email',
        quiz_answer: mail,
      },
    });
    if (!validationWarning) return;
    window.location = `${resultLink}`;
  }

  return (
    <div className={`${common.step} ${s.step} ${isThisCurrentStepClass} ${isThisStepCompletedClass}`}>
      <div className={`${common.stepTitle} ${s.stepTitle}`}>{T('customV1_email_title')}</div>
      <div className={s.subtitle}>{T('customV1_email_subtitle')}</div>
      <div className={s.inputWrapper}>
        <input
          className={s.value}
          id="mail"
          type="email"
          value={value}
          pattern=".+@globex\.com"
          autoComplete="true"
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={actionOnFocus}
          tabIndex={!isThisCurrentStep ? -1 : ''}
          ref={inputFocusRef}
        />
        {showSuggestions && (
          <ul className={s.inputDomainList}>
            {suggestions.map((suggestion) => (
              <li 
                key={suggestion}
              >
                <button
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {email.split('@')[0]}
                  {suggestion}
                </button>
              </li>
            ))}
          </ul>
        )}
        {
          validationWarning
          && (
            <div className={s.warning}>
              {validationWarning}
            </div>
          )
        }
      </div>
      <div className={s.description}>
        <div>
          <svg className={s.lock} alt="Lock icon" width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.00004 8.14836C0.894648 8.152 0 9.14095 0 10.3773V17.7711C0 19.0023 0.897353 20 2.00599 20H11.994C13.1021 20 14 19.0096 14 17.7711V10.3773C14 9.1484 13.1059 8.15132 12 8.14836V5.55497C12 2.48672 9.75587 0 6.99985 0C4.23849 0 1.99974 2.48672 1.99974 5.55497L2.00004 8.14836ZM4.00009 8.14836V5.55632C4.00009 3.71035 5.34343 2.22216 7.00015 2.22216C8.65344 2.22216 10.0002 3.7148 10.0002 5.55632V8.14836H4.00009Z" />
          </svg>
        </div>
        {T('customV1_email_description_2')}
      </div>
      <div className={s.actionBar}>
        <button
          ref={buttonRef}
          className={s.completeButton}
          type="button"
          onClick={goToPaymentPage}
          disabled={!currentStepCompleted}
        >
          {T('continue')}
        </button>
      </div>

    </div>
  );
}

export default Mail;
